import { toRecordSet } from './records/model.js';
import { hasDuplicateKeys, pickKeyColumn, recordColumns } from './records/keys.js';
import { buildRecordsResult } from './records/records-result.js';
import { rawLineRows } from './line-diff.js';
import { appendPropertyPath } from './records/path.js';

/** Compare decoded documents; parsing is shared with the single-file viewer. */
export function diffParquet({ leftDocument, rightDocument, keyColumn, options = {} } = {}) {
  if (!leftDocument || !rightDocument) throw new Error('Upload Parquet files to each pane to compare. Use the viewer to inspect a single file or dataset.');
  if (leftDocument.format !== 'parquet' || rightDocument.format !== 'parquet') throw new Error('Parquet comparison requires a Parquet file on both sides.');
  const common = leftDocument.columns.filter(c => rightDocument.columns.includes(c));
  const dataset = leftDocument.dataset || rightDocument.dataset;
  if (keyColumn && !common.includes(keyColumn)) throw new Error(`Key column “${keyColumn}” must exist in both datasets.`);
  let key = keyColumn === null ? null : pickKeyColumn(common, keyColumn);
  if (dataset && !key) throw new Error('Choose a key column present in both datasets. Dataset comparison matches by key, not file or row position.');
  const invalidKey = key && [...leftDocument.rows, ...rightDocument.rows].some(row => row[key] == null || row[key] === '' || typeof row[key] === 'object');
  let lm = toRecordSet(leftDocument.rows, { keyColumns: key ? [key] : [] });
  let rm = toRecordSet(rightDocument.rows, { keyColumns: key ? [key] : [] });
  let warning;
  if (dataset) {
    for (const [label, model, document] of [['Original', lm, leftDocument], ['Changed', rm, rightDocument]]) {
      const seen = new Map();
      const location = index => {
        const source = document.files?.find(f => index >= f.startRow && index < f.startRow + f.rowCount)?.path;
        return `row ${index + 1}${source ? ` in “${source}”` : ''}`;
      };
      for (const record of model.records) {
        const value = document.rows[record.index][key];
        if (value == null || value === '' || typeof value === 'object') throw new Error(`${label} dataset has a missing or non-scalar key in column “${key}” at ${location(record.index)}. Choose a complete, unique key; no comparison was produced.`);
        if (seen.has(record.key)) throw new Error(`${label} dataset has duplicate keys in column “${key}”: ${location(seen.get(record.key))} and ${location(record.index)}. Choose a unique key; no comparison was produced.`);
        seen.set(record.key, record.index);
      }
    }
  }
  if (invalidKey || hasDuplicateKeys(lm) || hasDuplicateKeys(rm)) {
    warning = { kind: invalidKey ? 'invalid-key' : 'duplicate-key', key };
    key = null;
    lm = toRecordSet(leftDocument.rows);
    rm = toRecordSet(rightDocument.rows);
  }
  const cols = doc => doc.rows.length ? recordColumns(doc.rows) : doc.columns.map(c => appendPropertyPath('', c));
  const result = buildRecordsResult({ lm, rm, options, leftCols: cols(leftDocument), rightCols: cols(rightDocument),
    // Array lengths and populated null structs affect display paths, not schema.
    schemaLeftCols: leftDocument.columns.map(c => appendPropertyPath('', c)),
    schemaRightCols: rightDocument.columns.map(c => appendPropertyPath('', c)),
    keyCol: key ? appendPropertyPath('', key) : null, format: 'parquet', warning,
    rawRows: rawLineRows(JSON.stringify(leftDocument.rows, null, 2), JSON.stringify(rightDocument.rows, null, 2), options) });
  result.keyColumn = key;
  if (invalidKey) result.changes[0] = { kind: 'invalid-key', key: warning.key };
  const rightSchema = new Map(rightDocument.schema.map(c => [JSON.stringify(c.path), c]));
  const schemaTypes = c => JSON.stringify(Object.fromEntries(Object.entries(c).filter(([k]) => !['name', 'path', 'field_id', 'num_children'].includes(k))));
  const typeChanges = leftDocument.schema.filter(c => rightSchema.has(JSON.stringify(c.path)) && schemaTypes(c) !== schemaTypes(rightSchema.get(JSON.stringify(c.path))));
  result.typeChanges = typeChanges.length;
  result.changes.unshift(...typeChanges.map(c => ({ kind: 'column-type', name: c.name, oldType: schemaTypes(c), newType: schemaTypes(rightSchema.get(JSON.stringify(c.path))) })));
  const leftSchema = new Map(leftDocument.schema.map(c => [JSON.stringify(c.path), c]));
  const addedFields = rightDocument.schema.filter(c => c.path.length > 1 && leftDocument.columns.includes(c.path[0]) && !leftSchema.has(JSON.stringify(c.path)));
  const removedFields = leftDocument.schema.filter(c => c.path.length > 1 && rightDocument.columns.includes(c.path[0]) && !rightSchema.has(JSON.stringify(c.path)));
  result.columnsAdded += addedFields.length;
  result.columnsRemoved += removedFields.length;
  result.changes.unshift(...addedFields.map(c => ({ kind: 'column-add', name: c.name })), ...removedFields.map(c => ({ kind: 'column-remove', name: c.name })));
  result.schema = { left: leftDocument.schema, right: rightDocument.schema };
  if (dataset) {
    // Part counts, filenames and record order are storage layout, not changes.
    result.moved = 0;
    result.changes = result.changes.filter(change => change.kind !== 'reorder');
    for (const row of result.render.rows) row.reordered = false;
    result.dataset = true;
  }
  return result;
}
